import {
	StepAccordion,
	AccordionMinimumHeight,
	ButtonColorEnum,
	AccordionBackgroundColor,
} from "../../../../Utils/StepAccordion";
import { BoxButton } from "../../../../Utils/UI/BoxButton/BoxButton";
import { InputComponent } from "../../../../Utils/UI/InputComponent/InputComponent";
import IStepInterface from "../../../../../Interfaces/IStepInterface";
import "./CohabitantStep.scss";
import Column from "../../../../Utils/Column/Column";
import { ValidateUserProfile } from "../../../../../Services/ValidationService/ValidationService";
import { useContext, useEffect, useRef, useState } from "react";
import PartnerContentContext from "../../../../../Contexts/PartnerContentContext";
import { MobileWrapper } from "../../../../Utils/MobileMargin/MobileMargin";
import CallMeUpComponent from "../../../../SharedComponents/CallMeUpComponent/CallMeUpComponent";
import { StepEnum } from "../../../../../Models/StepEnum";
import { Box, Dialog, DialogTitle, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { InfoTooltip } from "../../../../Utils/UI/InfoBadge/InfoBadge";
import { useOrder } from "../../../../../Contexts/OrderContext";

const CohabitantStep = ({
	nextStep,
	previousStep,
	open,
	toggleOpen,
	datahubAddress,
	setErrorMode,
}: IStepInterface) => {
	const { order, getOrder, updateOrder } = useOrder();
	const [allowNext, setAllowNext] = useState(true);
	const [idqTries, setIdqTries] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [cohabError, setCohabError] = useState("");
	const [firstButtonSelected, setFirstButtonSelected] = useState(false);
	const [secondButtonSelected, setSecondButtonSelected] = useState(false);
	const partnerContentContext = useContext(PartnerContentContext);
	const [keyboardPadding, setKeyboardPadding] = useState<number>(0);
	const DOMPurify = require("dompurify")(window);
	const [existingCustomerModalOpen, setExistingCustomerModalOpen] = useState(false);
	const validationRequestId = useRef(0);

	useEffect(() => {
		validationRequestId.current += 1;
		setIsLoading(false);
	}, [order.deliveryAddressId]);

	useEffect(() => {
		setExistingCustomerModalOpen(order.existingCustomer);
	}, [order.existingCustomer]);

	const handleCprChange = (val: { value: string; valid: boolean; error: string }): any => {
		updateOrder({
			cohabCpr: val.valid ? val.value : "",
			CohabIDQValid: false,
			CohabDatahubValid: false,
		});
		setAllowNext(true);
	};

	const handleNameChange = (val: { value: string; valid: boolean; error: string }): any => {
		updateOrder({
			cohabFullName: val.valid ? val.value : "",
			CohabIDQValid: false,
			CohabDatahubValid: false,
		});
		setAllowNext(true);
	};

	const formValid = () => {
		return secondButtonSelected || (!!order.cohabCpr && !!order.cohabFullName && allowNext);
	};

	const resolveValidation = (iDQResult: any, datahubResult: any) => {
		const CohabIDQValid = iDQResult.value.data === "Valid" || iDQResult.status === 500;
		const CohabDatahubValid =
			datahubResult.value.data?.validationResult === "Valid" || datahubResult.status === 500;
		updateOrder({ CohabIDQValid, CohabDatahubValid });
		const { InBinding, InDelivery } = getOrder();
		setIsLoading(false);
		if (iDQResult.value.status === 403) {
			setAllowNext(false);
			setCohabError("IDQError");
		} else if (
			CohabIDQValid &&
			CohabDatahubValid &&
			!InBinding &&
			(!InDelivery || (partnerContentContext.resaleEnabled && InDelivery))
		) {
			setCohabError("");
			nextStep();
		} else if (!CohabIDQValid) {
			setIdqTries(idqTries + 1);
			setAllowNext(false);
			if (idqTries >= 2) {
				setCohabError("IDQError");
			}
		} else if (!CohabDatahubValid) {
			setErrorMode("FAQ");
		} else if (InBinding || (!partnerContentContext.resaleEnabled && InDelivery)) {
			if (partnerContentContext.chargingStandProductIds.length === 0) {
				setErrorMode("ExistingCustomer");
			} else {
				updateOrder({ existingCustomer: true });
			}
		} else {
			setErrorMode("FAQ");
		}
	};

	const ValidateAndNext = async () => {
		if (order.cohabCpr === order.cpr){
			setAllowNext(false);
			setErrorMode("SameCpr");
		}
		else{
			setIsLoading(true);
			const requestId = ++validationRequestId.current;
			const deliveryAddressId = order.deliveryAddressId;
			const resolveCurrentAddressValidation = (iDQResult: any, datahubResult: any) => {
				if (
					requestId !== validationRequestId.current ||
					getOrder().deliveryAddressId !== deliveryAddressId
				) {
					return;
				}
				resolveValidation(iDQResult, datahubResult);
			};

			ValidateUserProfile(
				order.cohabCpr,
				order.cohabFullName,
				datahubAddress,
				order.deliveryAddressId,
				resolveCurrentAddressValidation,
				true,
				partnerContentContext.partnerId
			);
		}		
	};

	const retry = () => {
		setIdqTries(0);
		setCohabError("");
	};

	const handleTouchStart = () => {
		setKeyboardPadding(55);
	};

	return (
		<div>
			<StepAccordion
				loading={isLoading}
				loadingText={partnerContentContext.stepLoader.text}
				loadingTitle={partnerContentContext.stepLoader.title}
				label={partnerContentContext.cohabitantStep.title}
				open={open}
				click={toggleOpen}
				firstButtonLabel={
					partnerContentContext.cohabitantStep.backButton
						? partnerContentContext.cohabitantStep.backButton
						: "Tilbage"
				}
				firstButtonOutline={true}
				firstButton={previousStep}
				secondButtonLabel={
					partnerContentContext.cohabitantStep.nextButton
						? partnerContentContext.cohabitantStep.nextButton
						: "Næste"
				}
				secondButtonDisabled={!formValid()}
				secondButtonType={formValid() ? ButtonColorEnum.DARK_GREEN : ButtonColorEnum.GREY}
				secondButton={firstButtonSelected ? ValidateAndNext : () => setErrorMode("FAQ")}
				minimumHeight={AccordionMinimumHeight.SMALL}
				firstButtonId="NameCprStepReturn"
				secondButtonId="NameCprStepForward"
				secondButtonShadow={true}
				backgroundColor={AccordionBackgroundColor.GREY}
			>
				<Box marginLeft={"0.7rem"}>
					<p>{partnerContentContext.cohabitantStep.textOne}</p>

					<div className="movingin-wrapper">
						<BoxButton
							height={60}
							label={partnerContentContext.cohabitantStep.firstBoxButton}
							onClick={() => {
								setFirstButtonSelected(true);
								setSecondButtonSelected(false);
							}}
							selected={firstButtonSelected}
						/>

						<BoxButton
							height={60}
							label={partnerContentContext.cohabitantStep.secondBoxButton}
							onClick={() => {
								setFirstButtonSelected(false);
								setSecondButtonSelected(true);
							}}
							selected={secondButtonSelected}
						/>
					</div>
				</Box>

				{firstButtonSelected && (
					<div>
						<Box marginLeft={"0.7rem"}>
							<p>
								<b>{partnerContentContext.cohabitantStep.textTwo}</b>
							</p>
							<p>{partnerContentContext.cohabitantStep.textThree}</p>
						</Box>

						<Column fullWidth={true}>
							<Box sx={{ display: "flex", alignItems: "center" }}>
								<InputComponent
									touchStart={handleTouchStart}
									placeholder={
										partnerContentContext.cohabitantStep.cohabitantNamePlaceholder
											? partnerContentContext.cohabitantStep.cohabitantNamePlaceholder
											: "Samlevers Navn"
									}
									validationType="name"
									change={handleNameChange}
								/>

								<Box sx={{ padding: 0, margin: "0 0 0 1rem" }}>
									<InfoTooltip
										placement="right"
										message="Skriv samlevers navn, som det står på deres sundhedskort (sygesikringskort)."
									/>
								</Box>
							</Box>
						</Column>

						<Column fullWidthMobile={true}>
							<Box sx={{ display: "flex", alignItems: "center" }}>
								<InputComponent
									touchStart={handleTouchStart}
									placeholder={
										partnerContentContext.cohabitantStep.cohabitantCPRPlaceholder
											? partnerContentContext.cohabitantStep.cohabitantCPRPlaceholder
											: "Samlevers CPR-nummer"
									}
									validationType="cpr_age"
									change={handleCprChange}
								/>
								
								<Box sx={{ padding: 0, margin: "0 0 0 1rem" }}>
									<InfoTooltip
										placement="right"
										message="Norlys Energi bruger din samlevers CPR-nummer til kreditvurdering og oprettelse af dig/jer som kunde. Du kan finde mere information i de gældende betingelser."
									/>
								</Box>
							</Box>
						</Column>

						<MobileWrapper marginBottomViewheight={keyboardPadding}></MobileWrapper>
					</div>
				)}

				{!allowNext && !order.CohabIDQValid && (
					<div className="error-section">
						{partnerContentContext.cohabitantStep.cohabitantIDQUnvalid}
					</div>
				)}
			</StepAccordion>

			<Dialog open={cohabError === "IDQError"} onClose={() => { retry(); }} maxWidth="sm" fullWidth>
				<DialogTitle sx={{ m: 0, p: 1 }}>
					<IconButton onClick={() => { retry(); }} size="small" sx={{ position: "absolute", right: 8, top: 8 }}>
						<CloseIcon fontSize="small" />
					</IconButton>
				</DialogTitle>

				<div className="outer-container">
					<h1 className="headerText">{partnerContentContext.idqError.title}</h1>

					<div
						className="text"
						dangerouslySetInnerHTML={{
							__html: DOMPurify.sanitize(partnerContentContext.idqError.text),
						}}
					>
					</div>
					
					<CallMeUpComponent
						order={order}
						address={datahubAddress}
						showSecondButton={false}
						idqError={true}
						step={StepEnum.CohabitantStep}
					/>
				</div>
			</Dialog>

			<Dialog open={existingCustomerModalOpen} onClose={() => setExistingCustomerModalOpen(false)} maxWidth="sm" fullWidth>
				<DialogTitle sx={{ m: 0, p: 1 }}>
					<IconButton onClick={() => setExistingCustomerModalOpen(false)} size="small" sx={{ position: "absolute", right: 8, top: 8 }}>
						<CloseIcon fontSize="small" />
					</IconButton>
				</DialogTitle>

				<div className="outer-container">
					<h1 className="headerText">Eksisterende kunde</h1>
					
					<p style={{ fontSize: "18px" }}>
						Det ser ud som om du allerede har en el-aftale hos Norlys, vi kan derfor ikke tilbyde
						rabat på køb af el-aftale. Du vil stadig modtage rabat på køb af andre produkter.
					</p>
				</div>
			</Dialog>
		</div>
	);
};

export default CohabitantStep;
